import * as React from 'react';
import '../Assets/styles/component.css';
import { Flex, Box } from '../CommonUI/Layout';
import { Label, Link } from '../CommonUI';
import Button from '../CommonUI/Control/Button';
import { Text } from 'ilenia';
import { Settings, Widget } from '../Model';
import { StyleSheet, css } from 'aphrodite';
import { Common } from '../Assets/styles/common';
import { getTrustBoxList } from '../Api/trustboxClient';

interface TrustBoxProps {
    settings: Settings;
    parent?: Window | null;
    changeSettings: (settings: Settings, cb?: () => void) => void;
    tierName: string;
    locale: string;
    widgets: Widget[];
    setWidgets: (widgets: Widget[]) => void;
}

export class TrustBox extends React.Component<TrustBoxProps, {}> {
    constructor(props: TrustBoxProps) {
        super(props);
        this.onTryTrustboxClick = this.onTryTrustboxClick.bind(this);
        this.onPreviewClick = this.onPreviewClick.bind(this);
        this.setPreviewLocalStorage = this.setPreviewLocalStorage.bind(this);
    }

    public componentDidMount() {
        const {
            settings,
            locale,
            widgets,
            setWidgets
        } = this.props;
        if (widgets.length === 0) {
            getTrustBoxList(settings.general!.key!, locale)
                .then((responseWidgets) => {
                    setWidgets(responseWidgets);
                });
        }
        analytics.page();
    }

    public onPreviewClick() {
        const { trustbox } = this.props.settings;
        if (trustbox!.pageUrls!.landing) {
            window.open(trustbox!.pageUrls!.landing, '_blank');
        }
    }

    public getEditLink(url: string, page?: string) {
        try {
            const parsed = new URL(url);
            parsed.searchParams.append('tpTrustBoxPreview', 'true');
            if (page) {
                parsed.searchParams.append('page', page);
            }
            return parsed.toString();
        } catch (err) {
            return `${url}?tpTrustBoxPreview=true${page ? '&page=' + page : ''}`;
        }
    }

    public setPreviewLocalStorage() {
        localStorage.setItem('tpTrustBoxPreview', 'true');
    }

    public onTryTrustboxClick() {
        const { trustbox } = this.props.settings;
        if (trustbox!.pageUrls!.landing) {
            this.storeSettings();
            this.setPreviewLocalStorage();
            window.open(this.getEditLink(trustbox!.pageUrls!.landing), '_blank');
        }
    }

    public storeSettings() {
        if (window.localStorage) {
            const data = {
                loadData: this.props.settings,
                widgets: this.props.widgets,
            };
            localStorage.setItem('tp_settings_integration_app', JSON.stringify(data));
        }
    }

    public onChooseTrustBoxClick() {
        window.open(process.env.REACT_APP_SnippetUrl!, '_blank');
    }

    public renderTryTrustboxesScreen() {
        const { settings, tierName } = this.props;
        return (
            <main className={css(styles.container)}>
                <Flex>
                    <Box ml={20} mb={10} >
                        <Label label="trustBox.tryDragDrop" />
                    </Box>
                    <Box ml={20} mb={15} mt={0}>
                        <Flex flexDirection="row">
                            <Box width={40}>
                                <span className={css(styles.step)}>1</span>
                            </Box>
                            <Box width={1 - 40}>
                                <Box mt={1} mb={15}><Text id="trustBox.firstStep" /></Box>
                                <Box><img className={css(styles.image)} src="/dragndrop.png" /></Box>
                            </Box>
                        </Flex>
                    </Box>
                    <Box ml={20} mb={15} mt={0}>
                        <Flex flexDirection="row" >
                            <Box width={40}>
                                <span className={css(styles.step)}>2</span>
                            </Box>
                            <Box width={1 - 40}>
                                <Box mt={1} mb={15}><Text id="trustBox.secondStep" /></Box>
                                <Box><img className={css(styles.image)} src="/publish.png" /></Box>
                            </Box>
                        </Flex>
                    </Box>
                    <Button
                        onClick={this.onTryTrustboxClick}
                        track={{
                            placement: 'Bottom left corner',
                            action: 'Try drag and drop trustbox',
                            name: 'shopify-try-trustbox-button',
                            source: settings.source!,
                            tierName,
                        }}
                    >
                        <Text id="trustBox.addTrustboxes" />
                    </Button>
                </Flex>
            </main>
        );
    }

    public renderTrustBoxList() {
        const { settings, tierName } = this.props;
        const { trustbox } = settings;
        const trustboxes = trustbox!.trustboxes.map(({ widgetName, page }) => (
            { widgetName: widgetName!, page, pageUrl: trustbox!.pageUrls![page!] }
        ));
        const capitalizePages = new Set(['landing', 'category', 'product']);
        return (
            <main className={css(styles.container)}>
                <Flex>
                    <Box ml={20} mb={10} mt={22} >
                        <Label label="trustBox.listTitle" />
                    </Box>
                    <ul className={css(styles.ul)}>
                        {trustboxes.map((item: { widgetName: string, page: string, pageUrl: string }, index: number) => (
                            <li key={index} className={css(styles.li)}>
                                <Flex flexDirection="row" alignItems="center">
                                    <strong>{`${item.widgetName} - `}</strong>
                                    <span className={css(styles.page)}>
                                        {capitalizePages.has(item.page) ? item.page.charAt(0).toUpperCase() + item.page.slice(1) : item.pageUrl}
                                    </span>
                                    <Link
                                        className={css(styles.link)}
                                        mt={0}
                                        pl={0}
                                        label="trustBox.edit"
                                        href={this.getEditLink(item.pageUrl, item.page)}
                                        onClick={this.setPreviewLocalStorage}
                                        track={{
                                            placement: 'TrustBox list item',
                                            target: 'Storefront',
                                            name: 'shopify-edit-link',
                                            source: this.props.settings.source!,
                                            tierName,
                                        }}
                                    />
                                </Flex>
                            </li>
                        ))}
                    </ul>
                    <Flex flexDirection="row">
                        <Button
                            onClick={this.onPreviewClick}
                            secondaryButton={true}
                            width={'unset'}
                            pr={0}
                            track={{
                                placement: 'Bottom left corner',
                                action: 'View website',
                                name: 'shopify-preview-button',
                                source: this.props.settings.source!,
                                tierName,
                            }}
                        >
                            <Text id="trustBox.previewSite" />
                        </Button>
                        <Button
                            onClick={this.onTryTrustboxClick}
                            width={'unset'}
                            track={{
                                placement: 'Bottom left corner',
                                action: 'Edit trustboxes',
                                name: 'shopify-edit-trustboxes-button',
                                source: this.props.settings.source!,
                                tierName,
                            }}
                        >
                            <Text id="trustBox.editTrustboxes" />
                        </Button>
                    </Flex>
                </Flex>
            </main>
        );
    }

    public renderNoDragDropScreen() {
        const { settings, tierName } = this.props;
        return(
            <main className={css(styles.container)}>
                <Flex>
                    <Box ml={20} mb={0} mt={22} >
                        <Label label="trustBox.noDragDrop.title" />
                    </Box>
                    <Box mt={0} mb={0} ml={3}>
                        <img src={require('../Assets/images/trustbox.png')} className={css(styles.trustbox)}/>
                    </Box>
                    <Box ml={20}>
                        <ul className={css(styles.noDragDropSteps)}>
                            <li className={css(styles.li, styles.noDragDropStep)}>
                                <Text id="trustBox.noDragDrop.step1"/>
                            </li>
                            <li className={css(styles.li, styles.noDragDropStep)}>
                                <Text id="trustBox.noDragDrop.step2"/>
                            </li>
                            <li className={css(styles.li, styles.noDragDropStep)}>
                                <Text id="trustBox.noDragDrop.step3"/>
                            </li>
                        </ul>
                    </Box>
                    <Box>
                        <Button
                            onClick={this.onChooseTrustBoxClick}
                            track={{
                                placement: 'Bottom left corner',
                                action: 'Choose TrustBox',
                                name: 'bigcommerce-blueprint-choose-trustbox',
                                source: settings.source!,
                                tierName,
                            }}
                        >
                            <Text id="trustBox.noDragDrop.chooseTrustboxes" />
                        </Button>
                    </Box>
                </Flex>
            </main>
        );
    }

    public render() {
        const { source, stencilEnabled, trustbox } = this.props.settings;
        const renderDragDrop = () => {
            return trustbox!.trustboxes.length ? this.renderTrustBoxList() : this.renderTryTrustboxesScreen();
        };
        switch (source) {
            case 'Shopify':
            case 'Shopware':
                return renderDragDrop();
            case 'BigCommerce':
                return !stencilEnabled ? this.renderNoDragDropScreen() : renderDragDrop();
            default:
                return '';
        }
    }
}

const styles = StyleSheet.create({
    container: {
        borderRadius: Common.Borders.radius,
        backgroundColor: Common.Color.lightTextColor,
        border: Common.Borders.border,
        padding: '0px 30px 30px 30px'
    },
    step: {
        background: 'rgba(54, 140, 215, 0.1)',
        borderRadius: '50%',
        color: '#368cd7',
        display: 'inline-block',
        fontWeight: 'normal',
        lineHeight: '26px',
        fontFamily: 'Lato',
        fontSize: '14px',
        marginRight: '5px',
        textAlign: 'center',
        width: '26px',
    },
    ul: {
        padding: '0 0 0 20px',
    },
    li: {
        fontSize: '14px',
        fontFamily: 'Lato',
        marginBottom: '8px',
        lineHeight: '1.43',
        listStyleType: 'none',
        height: '20px',
        fontStyle: 'normal',
        fontStretch: 'normal',
        paddingLeft: 0,
    },
    page: {
        margin: '0 10px',
        color: '#a9a9a9',
    },
    link: {
        'display': 'inline',
        'textDecoration': 'none',
        ':visited' : {
            color: '#4a90e2',
        }
    },
    image: {
        width: '318px',
        height: 'auto'
    },
    trustbox: {
        width: '236px',
        height: 'auto',
    },
    noDragDropSteps: {
        listStyleType: 'none',
        padding: 0,
        margin: 0,
    },
    noDragDropStep: {
        'marginBottom': 0,
        ':before': {
            content: '"-"',
            marginRight: '3px',
            display: 'inline-block',
        }
    }
});



// WEBPACK FOOTER //
// ./src/Containers/TrustBox.tsx